import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, Mail, Phone, MapPin, Inbox, MessageCircle } from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { PageTransition } from "@/components/common/PageTransition";
import { Skeleton } from "@/components/common/Skeletons";
import { usePageHeading } from "@/components/layout/AppLayout";
import { demoApi } from "@/lib/api";
import { timeAgo, formatDateTime } from "@/lib/format";
import { TID } from "@/constants/testIds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const STATUS_OPTIONS = ["new", "contacted", "scheduled", "completed", "rejected", "onforce"];

const normalizeDemoStatus = (status) => {
  const value = (status || "").toString().trim().toUpperCase();
  switch (value) {
    case "NEW":
    case "PENDING":
      return "new";
    case "CONTACTED":
      return "contacted";
    case "SCHEDULED":
      return "scheduled";
    case "COMPLETED":
    case "DONE":
      return "completed";
    case "REJECTED":
      return "rejected";
    case "ONFORCE":
    case "HOLD":
      return "onforce";
    default:
      return value.toLowerCase();
  }
};

export default function DemoRequests() {
  usePageHeading("Demo Requests", "Manage inbound demo enquiries");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingScheduleId, setSavingScheduleId] = useState(null);
  const [sendingConfirmationId, setSendingConfirmationId] = useState(null);
  const [sortOrder, setSortOrder] = useState("latest");

  useEffect(() => {
    (async () => {
      try {
        const response = await demoApi.list();
        const normalized = (response || []).map((item) => ({
          ...item,
          id: item.id ?? item.demoRequestId,
          name: item.name ?? "Demo Request",
          clinic: item.clinic ?? item.clinicName ?? "",
          email: item.email ?? "",
          phone: item.phone ?? "",
          city: item.city ?? "",
          stateName: item.stateName ?? item.state ?? "",
          status: normalizeDemoStatus(item.status),
          message: item.notes ?? item.message ?? "",
          requestedAt: item.submittedAt ?? item.requestedAt ?? item.createdAt ?? new Date().toISOString(),
          interest: item.plan ?? item.interest ?? "",
          demoDate: item.demoDate ?? "",
          demoTime: item.demoTime ? item.demoTime.slice(0, 5) : "",
          demoMode: item.demoMode ?? "",
        }));
        setItems(normalized);
        // Initialize note drafts
        const drafts = {};
        normalized.forEach((it) => (drafts[it.id] = it.message || ""));
        setNoteDrafts(drafts);
      } catch {
        toast.error("Failed to load demo requests");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = async (id, status) => {
    setSavingId(id);
    setItems((prev) => prev.map((d) => (d.id === id ? { ...d, status } : d)));
    try {
      await demoApi.updateStatus(id, status);
      toast.success("Request updated", { description: `Marked as ${status}.` });
    } catch {
      toast.error("Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);

  const saveNote = async (id) => {
    setSavingNoteId(id);
    const txt = noteDrafts[id] ?? "";
    try {
      await demoApi.updateNotes(id, txt);
      setItems((prev) => prev.map((d) => (d.id === id ? { ...d, message: txt } : d)));
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNoteId(null);
    }
  };

  const saveSchedule = async (id) => {
    const item = items.find((entry) => entry.id === id);
    if (!item?.demoDate || !item?.demoTime || !item?.demoMode) {
      toast.error("Enter date, time, and demo mode first");
      return;
    }
    setSavingScheduleId(id);
    try {
      const response = await demoApi.updateSchedule(id, {
        status: "SCHEDULED",
        demoDate: item.demoDate,
        demoTime: item.demoTime,
        demoMode: item.demoMode,
      });
      const updated = response?.demoRequest;
      if (updated) {
        setItems((prev) => prev.map((entry) => entry.id === id ? {
          ...entry,
          status: normalizeDemoStatus(updated.status ?? "SCHEDULED"),
          demoDate: updated.demoDate ?? item.demoDate,
          demoTime: updated.demoTime ? updated.demoTime.slice(0, 5) : item.demoTime,
          demoMode: updated.demoMode ?? item.demoMode,
        } : entry));
      } else {
        setItems((prev) => prev.map((entry) => entry.id === id ? { ...entry, status: "scheduled" } : entry));
      }
      toast.success("Demo schedule saved");
    } catch {
      toast.error("Failed to save demo schedule");
    } finally {
      setSavingScheduleId(null);
    }
  };

  const sendConfirmation = async (id) => {
    setSendingConfirmationId(id);
    try {
      await demoApi.sendConfirmation(id);
      toast.success("Confirmation WhatsApp sent");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send confirmation");
    } finally {
      setSendingConfirmationId(null);
    }
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aTime = new Date(a.requestedAt).getTime();
      const bTime = new Date(b.requestedAt).getTime();
      return sortOrder === "latest" ? bTime - aTime : aTime - bTime;
    });
  }, [items, sortOrder]);

  return (
    <PageTransition className="space-y-6">
      <div data-testid={TID.demoPage} />
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={Inbox} title="No demo requests" description="New enquiries will appear here." />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Sort requests</p>
              <p className="text-xs text-muted-foreground">Show newest or oldest demo requests first.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">Order</span>
              <Select value={sortOrder} onValueChange={setSortOrder} className="w-40">
                <SelectTrigger className="h-10 border-border bg-secondary/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest" className="capitalize">Latest first</SelectItem>
                  <SelectItem value="oldest" className="capitalize">Oldest first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {sortedItems.map((d, i) => (
              <motion.div key={d.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <GlassCard className="flex h-full flex-col p-5" hover>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 font-heading font-semibold text-gold">
                      {d.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{d.clinic}</p>
                    </div>
                  </div>
                  <StatusBadge status={d.status} />
                </div>

                <p className="mt-4 text-sm text-muted-foreground">{d.message}</p>

                  <div className="mt-4 grid gap-1.5 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-gold" /> {d.email}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-gold" />
                    <a className="underline underline-offset-2" href={`tel:${d.phone}`}>{d.phone}</a>
                    <Button asChild size="sm" variant="outline" className="ml-1 h-7 px-2" title={`Call ${d.phone}`}>
                      <a href={`tel:${d.phone}`}><Phone className="h-3.5 w-3.5" /><span className="sr-only">Call clinic</span></a>
                    </Button>
                  </span>
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> {(() => {
                    const parts = [];
                    if (d.city) parts.push(d.city);
                    if (d.stateName) parts.push(d.stateName);
                    return parts.length ? parts.join(", ") : "—";
                  })()}</span>
                  <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-gold" /> Requested {timeAgo(d.requestedAt)} · {formatDateTime(d.requestedAt)} · Interested in {d.interest}</span>
                </div>

                <div className="mt-4 rounded-xl border border-border/70 bg-secondary/20 p-3">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <CalendarClock className="h-4 w-4 text-gold" /> Demo schedule
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <input
                      type="date"
                      value={d.demoDate}
                      onChange={(e) => setItems((prev) => prev.map((entry) => entry.id === d.id ? { ...entry, demoDate: e.target.value } : entry))}
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                      aria-label="Demo date"
                    />
                    <input
                      type="time"
                      value={d.demoTime}
                      onChange={(e) => setItems((prev) => prev.map((entry) => entry.id === d.id ? { ...entry, demoTime: e.target.value } : entry))}
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                      aria-label="Demo time"
                    />
                    <Select value={d.demoMode || undefined} onValueChange={(value) => setItems((prev) => prev.map((entry) => entry.id === d.id ? { ...entry, demoMode: value } : entry))}>
                      <SelectTrigger className="h-10 border-border bg-background"><SelectValue placeholder="Online or offline" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ONLINE">Online</SelectItem>
                        <SelectItem value="OFFLINE">Offline</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => saveSchedule(d.id)} disabled={savingScheduleId === d.id}>
                      {savingScheduleId === d.id ? "Saving..." : "Save schedule"}
                    </Button>
                    <Button size="sm" onClick={() => sendConfirmation(d.id)} disabled={sendingConfirmationId === d.id || !d.demoDate || !d.demoTime || !d.demoMode}>
                      <MessageCircle className="mr-1.5 h-4 w-4" />
                      {sendingConfirmationId === d.id ? "Sending..." : "Send confirmation"}
                    </Button>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-xs text-muted-foreground">Notes</label>
                  <textarea
                    value={noteDrafts[d.id] ?? ""}
                    onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [d.id]: e.target.value }))}
                    className="w-full mt-2 min-h-[80px] rounded-md border border-input bg-transparent p-2 text-sm text-foreground"
                    placeholder="Add notes about this request"
                  />
                  <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => {
                        setNoteDrafts((prev) => ({ ...prev, [d.id]: d.message || "" }))
                      }}
                      className="min-w-[96px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() => saveNote(d.id)}
                      disabled={savingNoteId === d.id}
                      className="min-w-[110px]"
                    >
                      {savingNoteId === d.id ? "Saving…" : "Save Notes"}
                    </Button>
                  </div>
                </div>

                <div className="mt-auto pt-5">
                  <Select value={d.status} onValueChange={(v) => update(d.id, v)} disabled={savingId === d.id}>
                    <SelectTrigger data-testid={TID.demoStatusSelect} className="h-10 border-border bg-secondary/30 capitalize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </div>
      )}
    </PageTransition>
  );
}
